import type { FeedbackDraft } from './model';
import type { FeedbackScope } from './repository';

const feedbackCategories = new Set([
  'performance',
  'error',
  'compatibility',
  'usability',
  'feature-request',
  'copy-design',
  'data-error',
  'safety-improvement',
]);

export type FeedbackDeviceDraft = {
  draft: FeedbackDraft;
  ownerId: string;
  scopeKey: string;
  source: 'device';
  status: 'draft';
  lastSavedAt: string;
  route: string;
};

function scopeKey(scope: FeedbackScope): string {
  if (scope.kind === 'tenant') return `${scope.tenantId}:tenant`;
  if (scope.kind === 'site') return `${scope.tenantId}:${scope.siteId}:site`;
  return `${scope.tenantId}:${scope.siteId}:${scope.contractorId}`;
}

export function feedbackDraftStorageKey(ownerId: string, scope: FeedbackScope): string {
  return `kop:feedback-draft:v1:${encodeURIComponent(ownerId)}:${encodeURIComponent(scopeKey(scope))}`;
}

export function readFeedbackDeviceDraft(
  storage: Pick<Storage, 'getItem'>,
  ownerId: string,
  scope: FeedbackScope,
): FeedbackDeviceDraft | null {
  try {
    const value = JSON.parse(
      storage.getItem(feedbackDraftStorageKey(ownerId, scope)) ?? 'null',
    ) as FeedbackDeviceDraft | null;
    if (
      !value ||
      value.ownerId !== ownerId ||
      value.scopeKey !== scopeKey(scope) ||
      value.source !== 'device' ||
      value.status !== 'draft' ||
      typeof value.lastSavedAt !== 'string' ||
      typeof value.route !== 'string' ||
      typeof value.draft?.description !== 'string' ||
      !feedbackCategories.has(value.draft.category) ||
      typeof value.draft.diagnosticsConsent !== 'boolean' ||
      typeof value.draft.screenshotConsent !== 'boolean' ||
      typeof value.draft.screenshotName !== 'string'
    )
      return null;
    return value;
  } catch {
    return null;
  }
}

export function saveFeedbackDeviceDraft(
  storage: Pick<Storage, 'setItem'>,
  input: {
    ownerId: string;
    scope: FeedbackScope;
    draft: FeedbackDraft;
    route: string;
    now: string;
  },
): FeedbackDeviceDraft {
  const value: FeedbackDeviceDraft = {
    draft: input.draft,
    ownerId: input.ownerId,
    scopeKey: scopeKey(input.scope),
    source: 'device',
    status: 'draft',
    lastSavedAt: input.now,
    route: input.route,
  };
  storage.setItem(feedbackDraftStorageKey(input.ownerId, input.scope), JSON.stringify(value));
  return value;
}

export function clearFeedbackDeviceDraft(
  storage: Pick<Storage, 'removeItem'>,
  ownerId: string,
  scope: FeedbackScope,
): void {
  storage.removeItem(feedbackDraftStorageKey(ownerId, scope));
}
